import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * MPCI shared hook for server-side paginated tables.
 *
 * MPCI API response shape:
 * { status, message, data: { content: [], totalElements, totalPages, ... } }
 *
 * Request body shape (flat, BE contract):
 * { page, size, sortBy, sortDir, <filterField>: value|null, ... }
 *
 * @param {string}  url           - POST list endpoint
 * @param {object}  defaultParams - initial request body
 */
const useMpciTableData = (url, defaultParams) => {
  const [data, setData] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [params, setParams] = useState(defaultParams)

  const paramsRef = useRef(defaultParams)
  const abortRef = useRef(null)

  const fetchPage = useCallback(
    (overrides = {}) => {
      if (abortRef.current) {
        abortRef.current.abort()
      }

      const controller = new AbortController()
      abortRef.current = controller

      setLoading(true)
      setError(null)

            const merged = { ...paramsRef.current, ...overrides }

      // Strip orgId when null — BE expects orgId absent (not null) to mean "no filter"
      const body = Object.fromEntries(
        Object.entries(merged).filter(([k, v]) => !(k === 'orgId' && v == null))
      )

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(body),
        signal: controller.signal
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.json()
        })
        .then((json) => {
          // Unwrap MPCI response: { status, message, data: { content, totalElements, totalPages } }
          setData(json?.data?.content ?? [])
          setTotalElements(json?.data?.totalElements ?? 0)
          setTotalPages(json?.data?.totalPages ?? 0)
          setLoading(false)
        })
        .catch((err) => {
          if (err.name === 'AbortError') return
          console.error('[useMpciTableData]', err)
          setError(err.message || 'Failed to load data')
          setLoading(false)
        })

      return () => controller.abort()
    },
    [url]
  )

  const updateParams = useCallback((updates) => {
    setParams((prev) => {
      const next = {
        ...prev,
        ...updates,
        page: Math.max(0, updates.page ?? prev?.page ?? 0),
        // preserve size key (BE contract) — guard against stale pageSize key
        size: updates.size ?? prev?.size ?? 10
      }
      paramsRef.current = next
      return next
    })
  }, [])

  // Auto-fetch on params change. defaultParams/params may be null — callers that need to
  // hold off the first fetch until some async prerequisite resolves (e.g. org scope) pass
  // null until ready, then call updateParams() once with the real params.
  useEffect(() => {
    if (!params) return
    fetchPage()
  }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

  return { data, totalElements, totalPages, loading, error, params, fetchPage, updateParams }
}

export default useMpciTableData
